import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { Location } from '../models/location.model';

import { CollectionSummaryDTO } from '../models/collectionSummaryDTO.model';
import { BACKEND_URI } from "../config/config-vars";

const BASE_URL = `${BACKEND_URI}/api/collections`;
const GENERATE_URL = `${BASE_URL}/generate`;
const LATEST_URL = `${BASE_URL}/latest`; // 👈 Add this line

export interface ManualGameDTO {
  gameName: string;
  cashIn: number;
  cashOut: number;
}

export interface MechanicalMeterDTO {
  meterIn: number;
  meterOut: number;
  /** ISO local datetime "YYYY-MM-DDTHH:mm:ss"; optional (server defaults to now) */
  readingTime?: string;
}


export interface GenerateCollectionRequest {
  locationId: string;
  previousDate: string; // ISO date string
  splitPercentage: number;
  manualGames?: ManualGameDTO[]; // <-- Add this line
  useMechanicalMeters?: boolean;   // ← add this
}

export interface MeterSnapshot {
  boardId: string;
  assetTag: string;
  prevIn?: number;
  prevOut?: number;
  prevTime?: string;
  currIn?: number;
  currOut?: number;
  currTime?: string;
}

export interface MeterRow {
  boardId: string;
  assetTag: string;
  gameName?: string;
  serialNumber?: string;
  prevIn?: number;  prevOut?: number;  prevTime?: string;
  latestIn?: number; latestOut?: number; latestTime?: string;
}

export interface BulkMeterUpsert {
  boardId: string;
  meterIn: number;
  meterOut: number;
  readingTime: string; // ISO "YYYY-MM-DDTHH:mm:ss"
}




@Injectable({
  providedIn: 'root'
})
export class CollectionService {
  private summariesCache: CollectionSummaryDTO[] | null = null;
  private summariesRequest$: Observable<CollectionSummaryDTO[]> | null = null;
  private summariesInFlightPromise: Promise<CollectionSummaryDTO[]> | null = null;
  private summariesCacheKey: string | null = null;
  constructor(private http: HttpClient) {}

  generateCollection(request: GenerateCollectionRequest): Observable<CollectionSummaryDTO> {
    return this.http.post<CollectionSummaryDTO>(
      GENERATE_URL,
      request
    );
  }

  // 👇 Add this method to fix the error
  getLatestCollection(locationId: string): Observable<{
    currentCollectionDate: string;
    splitPercentage: number;
  }> {
    return this.http.get<{
      currentCollectionDate: string;
      splitPercentage: number;
    }>(`${LATEST_URL}/${locationId}`);
  }

  getAllSummaries(forceRefresh = false, pageSize = 200, maxPages = 100, fromDate?: string, toDate?: string): Observable<CollectionSummaryDTO[]> {
    const key = `${fromDate || ''}|${toDate || ''}|${pageSize}|${maxPages}`;

    if (!forceRefresh && this.summariesCache && this.summariesCacheKey === key) {
      return of(this.summariesCache);
    }

    if (!forceRefresh && this.summariesInFlightPromise && this.summariesCacheKey === key) {
      return from(this.summariesInFlightPromise);
    }

    // Use paged fetching to reduce payload per request; still aggregates all data.
    this.summariesCacheKey = key;
    this.summariesInFlightPromise = this.fetchAllSummariesPaged(pageSize, maxPages, fromDate, toDate);
    this.summariesRequest$ = from(this.summariesInFlightPromise).pipe(
      tap(data => this.summariesCache = data),
      finalize(() => {
        this.summariesRequest$ = null;
        this.summariesInFlightPromise = null;
      }),
      shareReplay(1)
    );

    return this.summariesRequest$;
  }

  private async fetchAllSummariesPaged(pageSize: number, maxPages: number, fromDate?: string, toDate?: string): Promise<CollectionSummaryDTO[]> {
    const all: CollectionSummaryDTO[] = [];
    let page = 0;

    while (page < maxPages) {
      let params = new HttpParams()
        .set('page', page)
        .set('pageSize', pageSize);
      if (fromDate) params = params.set('from', fromDate);
      if (toDate) params = params.set('to', toDate);

      const pageData = await firstValueFrom(
        this.http.get<CollectionSummaryDTO[]>(`${BASE_URL}/summaries`, { params })
      );
      const items = pageData || [];
      all.push(...items);

      if (items.length < pageSize) {
        break; // last page reached
      }

      page += 1;
    }

    return all;
  }

  getSummariesPage(page: number, pageSize: number, month?: string, salesRep?: string): Observable<CollectionSummaryDTO[]> {
    let params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);
    if (month) params = params.set('month', month);
    if (salesRep) params = params.set('rep', salesRep);
    return this.http.get<CollectionSummaryDTO[]>(`${BASE_URL}/summaries`, { params });
  }
 updateAmountReceived(summary: CollectionSummaryDTO): Observable<any> {
 return this.http.put(`${BASE_URL}/${summary.id}/amount-received`, {
    amountReceived: summary.amountReceived
  });
}
deleteCollection(id: string): Observable<void> {
  return this.http.delete<void>(`${BASE_URL}/${id}`);
}

updateFields(id: string, payload: Partial<CollectionSummaryDTO>): Observable<any> {
  return this.http.put(`${BASE_URL}/${id}/update-fields`, payload);
}

exportToPdf(month: string, salesRep?: string): Observable<Blob> {
  const params = new HttpParams()
    .set('month', month)
    .set('rep', salesRep || '');
  return this.http.get(`${BASE_URL}/export/pdf`, { params, responseType: 'blob' });
}

exportToExcel(month: string, salesRep?: string): Observable<Blob> {
  const params = new HttpParams()
    .set('month', month)
    .set('rep', salesRep || '');
  return this.http.get(`${BASE_URL}/export/excel`, { params, responseType: 'blob' });
}

getPendingLocationsForCollector(): Observable<Location[]> {
  return this.http.get<Location[]>(`${BASE_URL}/pending-locations`);
}
// ↓ add inside the CollectionService class
upsertMechanicalMeter(boardId: string, payload: MechanicalMeterDTO) {
  return this.http.post<void>(
    `${BASE_URL}/boards/${boardId}/mechanical-meter`,
    payload
  );
}

getMetersSnapshot(locationId: string, previousDateIso: string) {
  const params = new HttpParams()
    .set('locationId', locationId)
    .set('previousDate', previousDateIso);
  return this.http.get<MeterSnapshot[]>(`${BASE_URL}/meters-snapshot`, { params });
}

getMechanicalMeters(locationId: string, previousDateISO: string) {
  const params = new HttpParams().set('previousDate', previousDateISO);
  return this.http.get<MeterRow[]>(
    `${BASE_URL}/locations/${locationId}/mechanical-meters`,
    { params }
  );
}

// was: this.http.post<void>(`${BASE_URL}/boards/mechanical-meters/bulk`, payload)

// collection.service.ts
bulkUpsertMechanicalMeters(payload: BulkMeterUpsert[]) {
  return this.http.post<void>(
    `${BASE_URL}/boards/mechanical-meters/bulk`,
    payload  // <-- raw array, not { items: payload }
  );
}



}
